import type { ReactEventHandler, ReactNode } from "react";
import uuid from "react-uuid";
import { create } from "zustand";

import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from "@const/meta";

export interface IModalButton {
  label: string;
  onClick?: (e?: ReactEventHandler<HTMLButtonElement>) => void;
}

export interface IModal {
  title: string;
  content: ReactNode | string;
  onClickClose?: (e?: ReactEventHandler<HTMLButtonElement>) => void;
  confirm?: IModalButton;
  cancel?: IModalButton;
  isHideCancelButton?: boolean; // 버튼 자체를 보여 줄지 말지 여부
}

export interface IToast {
  message?: string;
  id?: string;
}

export interface ISubModal {
  title: string;
  content: ReactNode | string;
  id?: string;
  handle?: string;
}

interface IMeta {
  title: string;
  description?: string;
}

export interface ILayoutStore {
  isAccountOpen: boolean;
  isCartOpen: boolean;
  isSearchOpen: boolean;
  isNavigationOpen: boolean;
  isMarketOpen: boolean;
  isMenuOpen: boolean;

  isShowFooter: boolean;
  isShowDim: boolean;

  modal: IModal | null;
  toast: IToast[];
  subModal: ISubModal[];

  meta: IMeta;
  isLoading: boolean;

  openToast: (toast: IToast) => void;
  closeToast: (toast: IToast[]) => void;
  openModal: (modal: IModal | null) => void;
  openCart: (isCartOpen: boolean) => void;
  openSearch: (isSearchOpen: boolean) => void;
  openAccount: (isAccountOpen: boolean) => void;
  openNavigation: (isNavigationOpen: boolean) => void;
  openMarket: (isMarketOpen: boolean) => void;
  openMenu: (isMenuOpen: boolean) => void;
  closeAll: () => void;

  openSubModal: (subModal: ISubModal[]) => void;
  closeSubModal: () => void;

  showDim: (isShowDim: boolean) => void;
  showFooter: (isShowFooter: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;

  setMeta: (meta: IMeta) => void;
  clearMeta: () => void;
}

const initLayout = {
  isAccountOpen: false,
  isCartOpen: false,
  isSearchOpen: false,
  isMarketOpen: false,
  isMenuOpen: false,
  isNavigationOpen: false,

  isShowDim: false,
  isShowFooter: true,
  isLoading: false,

  toast: [{ message: "dummy", id: "-1" }], // 에니메이션 때문에 더미 데이터 넣기
  subModal: [],
  modal: null,
  meta: { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
};

export const useLayoutStore = create<ILayoutStore>()((set) => ({
  // 초기값
  ...initLayout,

  // 함수
  showDim: (isShowDim: boolean) => {
    set(() => ({ isShowDim }));
  },
  openToast: (toast: IToast) => {
    const newToast = { ...toast, id: uuid() };
    set((state) => ({ toast: [...state.toast, newToast] }));

    // TODO 중간에 하나 닫으면 타이밍 느려짐 (setTimeout 클로저때문)
    setTimeout(() => {
      set((state) => ({ toast: [...state.toast.slice(0, 1), ...state.toast.slice(2)] }));
    }, 3000);
  },
  closeToast: (toast: IToast[]) => {
    set(() => ({ toast }));
  },
  openSubModal: (subModal: ISubModal[]) => {
    set(() => ({ subModal }));
  },
  closeSubModal: () => {
    set((state) => ({ subModal: [...state.subModal.slice(1)] }));
  },
  openModal: (modal: IModal | null) => {
    set(() => ({ modal }));
  },
  showFooter: (isShowFooter) => {
    set(() => ({ isShowFooter }));
  },
  openCart: (isCartOpen) => {
    set(() => ({ ...initLayout, isMenuOpen: isCartOpen, isCartOpen }));
  },
  openSearch: (isSearchOpen) => {
    set(() => ({ isMenuOpen: isSearchOpen, isSearchOpen }));
  },
  openAccount: (isAccountOpen) => {
    set(() => ({ isMenuOpen: isAccountOpen, isAccountOpen }));
  },
  openMarket: (isMarketOpen) => {
    set(() => ({ isMenuOpen: isMarketOpen, isMarketOpen }));
  },
  openNavigation: (isNavigationOpen) => {
    set(() => ({ isMenuOpen: isNavigationOpen, isNavigationOpen }));
  },
  openMenu: (isMenuOpen) => {
    set(() => ({ isMenuOpen }));
  },
  closeAll: () => {
    set((state) => ({
      ...initLayout,
      toast: state.toast,
      subModal: state.subModal,
      isShowFooter: state.isShowFooter,
      meta: state.meta,
    }));
  },
  setIsLoading: (isLoading: boolean) => {
    set(() => ({ isLoading }));
  },
  setMeta: (meta: IMeta) => {
    set(() => ({ meta }));
  },
  clearMeta: () => {
    set(() => ({ meta: initLayout.meta }));
  },
}));
